import { useEffect, useRef, useState } from 'react';
import { BIO_MAX_LENGTH, checkProfileLink, MAX_PROFILE_LINKS } from '@zip2live/shared';
import { Avatar, Footer, Header, Initial, Link, LiveBadge } from '../components/Brand.tsx';
import { CameraIcon, CameraPlusIcon, CloseIcon, LockIcon, TrashIcon } from '../components/Icons.tsx';
import { CategoryTag, count, duration } from '../components/StreamStats.tsx';
import { TitleEditor } from '../components/TitleEditor.tsx';
import { api } from '../lib/api.ts';
import { squarePhoto } from '../lib/photo.ts';
import { signOut, useMe, type Me } from '../lib/me.ts';
import { navigate } from '../lib/router.ts';
import { chatBans, chatMods } from '../lib/chat.ts';
import { BlockedList, DeleteAccount, RemovalNote, ReviewQueueLink, SuspensionBanner } from '../components/Safety.tsx';
import { useSafety } from '../lib/safety.ts';
import type { SafetyStatus } from '@zip2live/shared/safety';
import { AlertMethods } from '../components/FollowButton.tsx';
import { followApi, type MyFollow } from '../lib/follow.ts';
import { resumePath } from '../components/LiveReturn.tsx';

const ACTIVE = new Set(['CREATED', 'PREPARING', 'READY', 'CONNECTING', 'LIVE', 'RECONNECTING']);

const STATUS_LABEL: Record<string, string> = {
  CREATED: 'Link ready', PREPARING: 'Link ready', READY: 'Link ready', CONNECTING: 'Connecting',
  LIVE: 'Live', RECONNECTING: 'Reconnecting', ENDING: 'Ending', ENDED: 'Ended', FAILED: 'Did not start', EXPIRED: 'Unused',
};

type Stream = Me['streams'][number];

/**
 * The creator's home: who you are, your link, and every stream you started. Active streams can be
 * ended; finished ones deleted, and their replays made private (only the creator can watch).
 */
export function Account() {
  const { me, loaded, reload } = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Stream | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // Removed streams, a suspension, appeals, and whether you review reports (staff).
  const { safety, reload: reloadSafety } = useSafety();

  if (!loaded) return <Shell><p className="mt-6 font-semibold">Loading…</p></Shell>;
  const user = me?.user;

  const act = async (id: string, run: () => Promise<unknown>, failed: string) => {
    setBusy(id);
    setProblem(null);
    await run().catch(() => setProblem(failed));
    await reload();
    setBusy(null);
  };
  const end = (id: string) => act(id, () => api.endStream(id), 'Couldn’t end that stream. Try again.');
  const toggleVisibility = (s: Stream) =>
    act(s.id, () => api.setReplayVisibility(s.id, s.visibility === 'private' ? 'unlisted' : 'private'), 'Couldn’t change who can watch that replay. Try again.');
  const toggleListed = (s: Stream) =>
    act(s.id, () => api.setListed(s.id, s.visibility !== 'public'), 'Couldn’t change Live Now for that stream. Try again.');
  const remove = async (s: Stream) => {
    await act(s.id, () => api.deleteStream(s.id), 'Couldn’t delete that stream. Try again.');
    setConfirming(null);
  };
  // Errors show in the editor itself, so it stays open with the text.
  const retitle = async (s: Stream, title: string | null) => {
    await api.setTitle(s.id, title);
    await reload();
  };

  const name = me?.channel?.displayName ?? (me?.channel?.handle ? `@${me.channel.handle}` : null);

  return (
    <Shell>
      {user?.isVerified ? (
        <section className="mt-7 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-navy p-6 text-white sm:p-7">
          <div className="flex min-w-0 items-center gap-4">
            <Avatar name={name ?? user.email ?? 'Z'} photoUrl={me?.channel?.photoUrl} className="h-16 w-16 text-2xl" />
            <div className="min-w-0">
              {name && <p className="truncate text-xl font-extrabold sm:text-[22px]">{name}</p>}
              {me?.channel?.handle ? (
                <Link to={`/${me.channel.handle}`} className="block truncate font-mono text-muted-dark hover:text-white">zip2live.com/{me.channel.handle}</Link>
              ) : (
                <button type="button" className="mt-1 rounded-lg bg-white px-3.5 py-2 text-sm font-extrabold text-navy" onClick={() => navigate('/claim')}>
                  Claim your personal link
                </button>
              )}
              <p className="mt-1 truncate text-[13px] text-faint-dark">Signed in as {user.email ?? user.phoneNumber}</p>
            </div>
          </div>
          <Actions />
        </section>
      ) : (
        <section className="mt-7 rounded-3xl bg-navy p-6 text-white sm:p-7">
          <p className="text-xl font-extrabold">You’re streaming as a guest on this device.</p>
          <p className="mt-2 max-w-xl text-muted-dark">Sign in with your email or phone to keep these streams on every device, keep replays for 30 days instead of 24 hours, and claim zip2live.com/yourname.</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button type="button" className="h-12 rounded-xl bg-white px-5 font-extrabold text-navy hover:bg-mist" onClick={() => navigate('/signin')}>
              Sign in
            </button>
            <Actions />
          </div>
        </section>
      )}

      {safety?.suspension && <SuspensionBanner suspension={safety.suspension} onAppealed={() => void reloadSafety()} />}
      {safety?.staff && <ReviewQueueLink />}

      <h2 className="speed-text mt-12 text-[28px]">Your streams</h2>
      {problem && <p role="alert" className="mt-3 font-bold text-race">{problem}</p>}
      <StreamList
        streams={me?.streams ?? []} busy={busy} onEnd={end} onVisibility={toggleVisibility} onListed={toggleListed} canList={Boolean(user?.isVerified)} onDelete={setConfirming}
        removals={safety?.removals ?? []} onAppealed={() => void reloadSafety()} onRetitle={retitle}
      />
      {confirming && <ConfirmDelete stream={confirming} busy={busy === confirming.id} onCancel={() => setConfirming(null)} onConfirm={() => void remove(confirming)} />}
      {user?.isVerified && me?.channel?.handle && <ChannelEditor channel={{ ...me.channel, handle: me.channel.handle }} reload={reload} />}
      {user && <Following />}
      {user && <ChatMods />}
      {user && <ChatBans />}
      {user && <BlockedList />}
      {user?.isVerified && (
        <button type="button" className="mt-6 text-sm font-bold underline underline-offset-2" onClick={() => void signOut().then(() => navigate('/'))}>
          Sign out
        </button>
      )}
      {user && <DeleteAccount guest={!user.isVerified} />}
    </Shell>
  );
}

/** "example.com/me" → "https://example.com/me"; anything with a scheme is left for the check to judge. */
const withScheme = (link: string) => (/^[a-z][a-z0-9+.-]*:\/\//i.test(link) ? link : `https://${link}`);

const LINK_PROBLEM = { not_https: 'Use an https:// link.', too_long: 'That link is too long.', invalid: 'That doesn’t look like a web address.' } as const;

type ChannelInfo = NonNullable<Me['channel']> & { handle: string };

/**
 * Your channel: what zip2live.com/<handle> shows. Photo (cropped and resized here, checked by the
 * API before anyone sees it), display name, bio and links. Signed-in creators with a handle only.
 */
function ChannelEditor({ channel, reload }: { channel: ChannelInfo; reload: () => Promise<unknown> }) {
  const [displayName, setDisplayName] = useState(channel.displayName ?? '');
  const [bio, setBio] = useState(channel.bio ?? '');
  const [links, setLinks] = useState<string[]>(channel.links.length ? channel.links.map((l) => l.url) : ['']);
  const [touched, setTouched] = useState<boolean[]>([]);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [photoBusy, setPhotoBusy] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const section = useRef<HTMLElement>(null);

  // zip2live.com/account#channel (the channel page's Edit channel button) lands here.
  useEffect(() => {
    if (location.hash === '#channel') section.current?.scrollIntoView({ block: 'start' });
  }, []);

  const problems = links.map((l) => {
    const check = l.trim() ? checkProfileLink(withScheme(l.trim())) : null;
    return check && !check.ok ? LINK_PROBLEM[check.reason] : null;
  });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(links.map(() => true));
    if (problems.some(Boolean)) return;
    setSaving(true);
    setNote(null);
    try {
      const saved = await api.updateChannel({
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        bio,
        links: links.map((l) => l.trim()).filter(Boolean).map(withScheme),
      });
      // Show what was stored: https:// added, duplicates and blanks dropped, bio tidied.
      setLinks(saved.links.length ? saved.links.map((l) => l.url) : ['']);
      setBio(saved.bio ?? '');
      setTouched([]);
      await reload();
      setNote({ ok: true, text: 'Saved. Your channel is up to date.' });
    } catch (err) {
      setNote({ ok: false, text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const pickPhoto = async (picked: File | undefined) => {
    if (!picked) return;
    setPhotoError(null);
    setPhotoBusy('Checking your photo…');
    try {
      const photo = await squarePhoto(picked);
      await api.uploadPhoto(photo);
      await reload();
    } catch (err) {
      setPhotoError((err as Error).message);
    } finally {
      setPhotoBusy(null);
      if (file.current) file.current.value = '';
    }
  };
  const removePhoto = async () => {
    setPhotoError(null);
    setPhotoBusy('Removing…');
    await api.removePhoto().then(reload, (err: Error) => setPhotoError(err.message));
    setPhotoBusy(null);
  };

  const name = displayName.trim() || channel.displayName || `@${channel.handle}`;
  const input = 'h-12 w-full min-w-0 rounded-xl border-2 border-line bg-white px-3.5 text-[16px] outline-none focus:border-navy';
  const label = 'block text-sm font-extrabold';
  return (
    <section id="channel" ref={section} className="scroll-mt-6" aria-labelledby="channel-title">
      <div className="mt-12 flex flex-wrap items-end justify-between gap-3">
        <h2 id="channel-title" className="speed-text text-[28px]">Your channel</h2>
        <Link to={`/${channel.handle}`} className="text-sm font-bold underline underline-offset-2 hover:text-race">
          View zip2live.com/{channel.handle}
        </Link>
      </div>
      <p className="mt-2 max-w-2xl text-muted">What people see on your channel page and next to your streams.</p>
      <div className="mt-4 rounded-3xl border border-line bg-white p-5 sm:p-7">
        <div className="flex flex-wrap items-center gap-4 sm:gap-5">
          <Avatar name={name} photoUrl={channel.photoUrl} className="h-20 w-20 text-3xl sm:h-24 sm:w-24 sm:text-4xl" />
          <div className="min-w-0 flex-1">
            <p className="font-extrabold">Profile photo</p>
            <p className="text-sm text-muted">A square from the middle of your picture. We check every photo before it goes up.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input ref={file} type="file" accept="image/*" className="sr-only" id="photo-file" onChange={(e) => void pickPhoto(e.target.files?.[0])} disabled={Boolean(photoBusy)} />
              <label htmlFor="photo-file" className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg bg-navy px-4 text-sm font-extrabold text-white hover:bg-navy-700 ${photoBusy ? 'pointer-events-none opacity-60' : ''}`}>
                <CameraPlusIcon />
                {channel.photoUrl ? 'Change photo' : 'Add a photo'}
              </label>
              {channel.photoUrl && (
                <button type="button" disabled={Boolean(photoBusy)} onClick={() => void removePhoto()} className="h-10 rounded-lg border-2 border-line px-4 text-sm font-bold hover:bg-mist disabled:opacity-60">
                  Remove
                </button>
              )}
            </div>
            {photoBusy && <p className="mt-2 text-sm font-bold text-muted" role="status">{photoBusy}</p>}
            {photoError && <p className="mt-2 text-sm font-bold text-race" role="alert">{photoError}</p>}
          </div>
        </div>

        <form onSubmit={(e) => void save(e)} className="mt-7 grid gap-6 border-t border-line pt-6">
          <div>
            <label htmlFor="channel-name" className={label}>Display name</label>
            <input id="channel-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={50} placeholder={`@${channel.handle}`} className={`${input} mt-2 max-w-md`} />
          </div>
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor="channel-bio" className={label}>Bio</label>
              <span className={`text-xs font-bold tabular-nums ${bio.length > BIO_MAX_LENGTH - 20 ? 'text-race' : 'text-muted'}`}>{bio.length}/{BIO_MAX_LENGTH}</span>
            </div>
            <textarea
              id="channel-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={BIO_MAX_LENGTH}
              rows={4}
              placeholder="What you stream, and when."
              className="mt-2 w-full resize-y rounded-xl border-2 border-line bg-white px-3.5 py-3 text-[16px] leading-relaxed outline-none focus:border-navy"
            />
          </div>
          <fieldset>
            <legend className={label}>Links</legend>
            <p className="mt-1 text-sm text-muted">Up to {MAX_PROFILE_LINKS}, https only. They show as the site’s name.</p>
            <div className="mt-2 grid gap-2">
              {links.map((l, i) => (
                <div key={i}>
                  <div className="flex gap-2">
                    <input
                      value={l}
                      onChange={(e) => setLinks(links.map((x, j) => (j === i ? e.target.value : x)))}
                      onBlur={() => setTouched((t) => Object.assign([...t], { [i]: true }))}
                      aria-label={`Link ${i + 1}`}
                      aria-invalid={Boolean(touched[i] && problems[i])}
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      placeholder="https://"
                      className={`${input} flex-1 ${touched[i] && problems[i] ? '!border-race' : ''}`}
                    />
                    <button
                      type="button"
                      aria-label={`Remove link ${i + 1}`}
                      onClick={() => {
                        setLinks(links.length > 1 ? links.filter((_, j) => j !== i) : ['']);
                        setTouched(touched.filter((_, j) => j !== i));
                      }}
                      className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2 border-line text-muted hover:bg-mist"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  {touched[i] && problems[i] && <p className="mt-1 text-sm font-bold text-race">{problems[i]}</p>}
                </div>
              ))}
            </div>
            {links.length < MAX_PROFILE_LINKS && (
              <button type="button" onClick={() => setLinks([...links, ''])} className="mt-2 h-10 rounded-lg border-2 border-dashed border-line px-4 text-sm font-bold text-navy hover:bg-mist">
                + Add a link
              </button>
            )}
          </fieldset>
          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" disabled={saving} className="h-12 rounded-xl bg-navy px-6 font-extrabold text-white hover:bg-navy-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {note && <p role={note.ok ? 'status' : 'alert'} className={`font-bold ${note.ok ? 'text-ok' : 'text-race'}`}>{note.text}</p>}
          </div>
        </form>
      </div>
    </section>
  );
}

/**
 * Channels you follow, with alerts on/off for each, and how you're told (this browser, email,
 * text). Guests have this too: their follows move to the account when they sign in.
 */
function Following() {
  const [follows, setFollows] = useState<MyFollow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const intent = new URLSearchParams(location.search).get('alerts');
  useEffect(() => {
    void followApi.mine().then((r) => setFollows(r.follows), () => setFollows([]));
  }, []);
  const update = async (f: MyFollow, fn: () => Promise<unknown>, next: MyFollow[]) => {
    setProblem(null);
    try {
      await fn();
      setFollows(next);
    } catch {
      setProblem(`Couldn’t change ${f.displayName ?? f.handle ?? 'that channel'}. Try again.`);
    }
  };
  return (
    <>
      <h2 className="speed-text mt-12 text-[28px]">Following</h2>
      <div className="mt-4 rounded-3xl border border-line bg-white px-5 py-3 sm:px-6">
        <p className="pt-1 text-xs font-extrabold tracking-[0.08em] text-muted uppercase">How we tell you someone went live</p>
        <AlertMethods intent={intent === 'email' || intent === 'sms' ? intent : null} returnTo="/account" className="mt-1" />
      </div>
      {problem && <p role="alert" className="mt-3 font-bold text-race">{problem}</p>}
      {follows && follows.length === 0 && (
        <p className="mt-4 rounded-3xl border border-line bg-white p-6 text-muted">You don’t follow anyone yet. Tap Follow on a stream to be told when they go live.</p>
      )}
      {follows && follows.length > 0 && (
        <ul className="mt-4 divide-y divide-line rounded-3xl border border-line bg-white">
          {follows.map((f) => {
            const name = f.displayName ?? (f.handle ? `@${f.handle}` : 'A guest creator');
            return (
              <li key={f.id} className="flex flex-wrap items-center gap-x-4 gap-y-2.5 px-5 py-3.5">
                <Initial name={name} />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-bold">
                    {f.handle ? <Link to={`/${f.handle}`} className="truncate hover:text-race">{name}</Link> : <span className="truncate">{name}</span>}
                    {f.liveId && (
                      <Link to={`/s/${f.liveId}`} aria-label={`${name} is live: watch`}>
                        <LiveBadge small />
                      </Link>
                    )}
                  </p>
                  <p className="text-[13px] text-muted">Following since {new Date(f.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex w-full gap-2 sm:w-auto">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={f.notify}
                    aria-label={`Alerts from ${name}`}
                    className={`h-10 shrink-0 rounded-lg border-2 px-3.5 text-sm font-extrabold ${f.notify ? 'border-navy bg-navy text-white' : 'border-line text-muted hover:border-navy'}`}
                    onClick={() => void update(f, () => followApi.setNotify(f.id, !f.notify), follows.map((x) => (x.id === f.id ? { ...x, notify: !f.notify } : x)))}
                  >
                    {f.notify ? 'Alerts on' : 'Alerts off'}
                  </button>
                  <button
                    type="button"
                    className="h-10 shrink-0 rounded-lg border-2 border-line px-3.5 text-sm font-extrabold hover:bg-mist"
                    onClick={() => void update(f, () => followApi.remove(f.id), follows.filter((x) => x.id !== f.id))}
                  >
                    Unfollow
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/**
 * Chat moderators: anyone can have them (guests too), but a mod needs a ZIP2LIVE account, so they
 * are added by @handle. Mods can delete, time out, ban, pin and change chat settings on all your streams.
 */
function ChatMods() {
  const [mods, setMods] = useState<{ handle: string; createdAt: number }[] | null>(null);
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void chatMods.list().then((r) => setMods(r.mods), () => setMods([]));
  }, []);
  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handle.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const added = await chatMods.add(handle.trim());
      setMods((all) => [...(all ?? []).filter((m) => m.handle !== added.handle), { handle: added.handle, createdAt: Date.now() }]);
      setHandle('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <h2 className="speed-text mt-12 text-[28px]">Chat moderators</h2>
      <p className="mt-2 max-w-2xl text-muted">
        Mods can delete messages, time people out, ban, pin and slow the chat on all your streams. They need a ZIP2LIVE account: add them by their @handle, or tap one of their messages in chat and choose Make mod.
      </p>
      <form onSubmit={(e) => void add(e)} className="mt-4 flex max-w-lg gap-2">
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          aria-label="Moderator’s @handle"
          placeholder="@handle"
          autoCapitalize="none"
          autoCorrect="off"
          className="h-12 min-w-0 flex-1 rounded-xl border-2 border-line bg-white px-3.5 text-[16px] outline-none focus:border-navy"
        />
        <button type="submit" disabled={busy || !handle.trim()} className="h-12 shrink-0 rounded-xl bg-navy px-5 font-extrabold text-white hover:bg-navy-700 disabled:opacity-50">
          Add mod
        </button>
      </form>
      {error && <p role="alert" className="mt-2 font-bold text-race">{error}</p>}
      {mods && mods.length > 0 && (
        <ul className="mt-4 divide-y divide-line rounded-3xl border border-line bg-white">
          {mods.map((m) => (
            <li key={m.handle} className="flex items-center justify-between gap-4 px-5 py-3.5">
              <div className="min-w-0">
                <p className="truncate font-bold">@{m.handle}</p>
                <p className="text-[13px] text-muted">Mod since {new Date(m.createdAt).toLocaleDateString()}</p>
              </div>
              <button
                type="button"
                className="h-10 shrink-0 rounded-lg border-2 border-line px-4 text-sm font-extrabold hover:bg-mist"
                onClick={() => void chatMods.remove(m.handle).then(() => setMods((all) => (all ?? []).filter((x) => x.handle !== m.handle)))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** People banned from this creator's chat, with Unban. Hidden when nobody is banned. */
function ChatBans() {
  const [bans, setBans] = useState<{ key: string; name: string; createdAt: number }[]>([]);
  useEffect(() => {
    void chatBans.list().then((r) => setBans(r.bans), () => undefined);
  }, []);
  if (!bans.length) return null;
  return (
    <>
      <h2 className="speed-text mt-12 text-[28px]">Banned from your chat</h2>
      <ul className="mt-4 divide-y divide-line rounded-3xl border border-line bg-white">
        {bans.map((b) => (
          <li key={b.key} className="flex items-center justify-between gap-4 px-5 py-3.5">
            <div className="min-w-0">
              <p className="truncate font-bold">{b.name}</p>
              <p className="text-[13px] text-muted">Banned {new Date(b.createdAt).toLocaleDateString()}</p>
            </div>
            <button
              type="button"
              className="h-10 shrink-0 rounded-lg border-2 border-line px-4 text-sm font-extrabold hover:bg-mist"
              onClick={() => void chatBans.lift(b.key).then(() => setBans((all) => all.filter((x) => x.key !== b.key)))}
            >
              Unban
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function Actions() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="btn-race shadow-race-deep h-13 px-7 text-[22px]" onClick={() => navigate('/go-live')}>GO LIVE</button>
      <Link to="/obs" className="inline-flex h-13 items-center rounded-xl border-2 border-line-dark px-5 font-extrabold text-white hover:bg-white/10">OBS setup</Link>
    </div>
  );
}

function StreamList({ streams, busy, onEnd, onVisibility, onListed, canList, onDelete, removals, onAppealed, onRetitle }: {
  streams: Stream[];
  busy: string | null;
  onEnd: (id: string) => void;
  onVisibility: (s: Stream) => void;
  onListed: (s: Stream) => void;
  canList: boolean;
  onDelete: (s: Stream) => void;
  /** Why each removed stream was removed, and its appeal. */
  removals: SafetyStatus['removals'];
  onAppealed: () => void;
  onRetitle: (s: Stream, title: string | null) => Promise<void>;
}) {
  if (!streams.length) return <p className="mt-3 rounded-3xl border border-line bg-white p-6 text-muted">Nothing yet. Tap GO LIVE to start.</p>;
  const action = 'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border-2 border-line px-3 text-sm font-bold disabled:opacity-50';
  return (
    <ul className="mt-4 divide-y divide-[#eef1f6] overflow-hidden rounded-3xl border border-line bg-white">
      {streams.map((s) => {
        const replay = s.status === 'ENDED' && s.has_replay && !s.removed;
        const isPrivate = s.visibility === 'private';
        return (
          <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-2.5 px-4 py-4 sm:flex-nowrap sm:gap-4 sm:px-5">
            {/* The latest preview picture, with the status on it. */}
            <Link to={`/s/${s.id}`} aria-label={s.title ? `Open “${s.title}”` : 'Open this stream'} className="relative aspect-video w-24 shrink-0 overflow-hidden rounded-lg bg-mist sm:w-32">
              {s.thumbnailUrl ? (
                <img src={s.thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-muted/35"><CameraIcon /></span>
              )}
              <span className="absolute top-1 left-1">
                {s.status === 'LIVE' && !s.removed ? (
                  <LiveBadge small />
                ) : (
                  <span className={`inline-block rounded-md px-1.5 py-0.5 text-[11px] font-extrabold ${s.thumbnailUrl ? 'bg-navy/80 text-white' : 'bg-white text-muted'}`}>
                    {s.removed ? 'Removed' : (STATUS_LABEL[s.status] ?? s.status)}
                  </span>
                )}
              </span>
              {replay && isPrivate && (
                <span className="absolute right-1 bottom-1 inline-flex items-center gap-1 rounded-md bg-navy px-1.5 py-0.5 text-[11px] font-extrabold text-white">
                  <LockIcon size={11} /> Private
                </span>
              )}
            </Link>
            <div className="min-w-0 flex-1">
              {s.removed ? (
                <Link to={`/s/${s.id}`} className="block truncate font-extrabold hover:text-race">{s.title ?? `zip2live.com/s/${s.id}`}</Link>
              ) : (
                <TitleEditor
                  compact
                  title={s.title}
                  onSave={(title) => onRetitle(s, title)}
                  display={
                    <Link to={`/s/${s.id}`} className="flex min-w-0 items-center gap-2 hover:text-race">
                      <span className="truncate font-extrabold">{s.title ?? `zip2live.com/s/${s.id}`}</span>
                      <CategoryTag category={s.category} tone="light" />
                      {s.visibility === 'public' && ACTIVE.has(s.status) && <span className="shrink-0 rounded-full bg-race/10 px-2.5 py-0.5 text-xs font-extrabold text-race">On Live Now</span>}
                    </Link>
                  }
                />
              )}
              <Link to={`/s/${s.id}`} className="block truncate text-sm text-muted hover:text-race">
                {new Date(s.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {s.source === 'obs' ? 'OBS' : 'Browser'}
                {replay && s.replay_expires_at && ` · replay until ${until(s.replay_expires_at)}`}
                {s.replay_expired && ' · replay expired'}
                {(s.views > 0 || s.peak_viewers > 0) && ` · ${count(s.views)} ${s.views === 1 ? 'view' : 'views'} · peak ${count(s.peak_viewers)}`}
                {s.status === 'ENDED' && duration(s.started_at, s.ended_at) && ` · ${duration(s.started_at, s.ended_at)}`}
                {(s.likes > 0 || s.dislikes > 0) && ` · ${count(s.likes)} 👍 ${count(s.dislikes)} 👎`}
                {s.reactions > 0 && ` · ${count(s.reactions)} reactions`}
                {replay && isPrivate && ' · only you can watch'}
              </Link>
              {s.removed && removals.find((r) => r.streamId === s.id) && <RemovalNote removal={removals.find((r) => r.streamId === s.id)!} onAppealed={onAppealed} />}
            </div>
            {ACTIVE.has(s.status) ? (
              <div className="flex shrink-0 flex-wrap gap-2">
                {/* A browser stream that's still on (closed tab, left to sign in): pick it back up. OBS reconnects by itself. */}
                {s.source !== 'obs' && (
                  <Link to={resumePath(s.id)} className="speed-text inline-flex h-10 shrink-0 items-center rounded-lg bg-race px-4 text-sm text-white hover:bg-race-600">
                    Back to stream
                  </Link>
                )}
                {canList && (
                  <button type="button" disabled={busy === s.id} className={`${action} text-navy hover:border-navy`} onClick={() => onListed(s)}>
                    {s.visibility === 'public' ? 'Unlist' : 'Show on Live Now'}
                  </button>
                )}
                <button type="button" disabled={busy === s.id} className="speed-text h-10 shrink-0 rounded-lg border-2 border-race px-4 text-sm text-race hover:bg-race hover:text-white disabled:opacity-50" onClick={() => onEnd(s.id)}>
                  END
                </button>
              </div>
            ) : (
              // Their own line on phones, at the end of the row on wider screens.
              <div className="flex w-full gap-2 sm:w-auto">
                {replay && (
                  <button type="button" disabled={busy === s.id} className={`${action} text-navy hover:border-navy`} onClick={() => onVisibility(s)}>
                    <LockIcon open={isPrivate} />
                    {isPrivate ? 'Unhide replay' : 'Make private'}
                  </button>
                )}
                <button type="button" disabled={busy === s.id} className={`${action} text-race hover:border-race`} onClick={() => onDelete(s)}>
                  <TrashIcon />
                  Delete
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ConfirmDelete({ stream, busy, onCancel, onConfirm }: { stream: Stream; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const replay = stream.status === 'ENDED' && stream.has_replay && !stream.removed;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="delete-title" onClick={onCancel}>
      <div className="w-full max-w-md rounded-t-[28px] bg-white p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-navy sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <p id="delete-title" className="speed-text text-2xl">Delete this stream?</p>
        <p className="mt-1 text-[15px] text-muted">
          {replay ? 'The replay is removed for everyone, including you, and the link stops working.' : 'It leaves your list and the link stops working.'} This can’t be undone.
        </p>
        <button type="button" disabled={busy} className="btn-race mt-5 h-12 w-full text-lg" onClick={onConfirm}>
          {busy ? 'DELETING…' : 'DELETE'}
        </button>
        <button type="button" className="mt-3 h-11 w-full font-bold text-muted underline underline-offset-2" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** "Nov 18", or "Oct 20, 7:15 AM" when it's within two days (guest replays last 24 hours). */
function until(at: number) {
  const soon = at - Date.now() < 2 * 86_400_000;
  return new Date(at).toLocaleString([], { month: 'short', day: 'numeric', ...(soon ? { hour: 'numeric', minute: '2-digit' } : {}) });
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-full bg-paper">
      <Header tone="paper" />
      <div className="mx-auto max-w-4xl px-5 pt-8 pb-20 sm:px-8 sm:pt-12">
        <h1 className="speed-text text-[clamp(2.5rem,7vw,3.25rem)] leading-none">Your ZIP2LIVE</h1>
        {children}
      </div>
      <Footer />
    </main>
  );
}
